import { useEffect, useState } from 'react';
import { crmApi, type ClientWithDeals, type DealSummary } from './api';

/**
 * "Existing deals" — one card per client, each listing that client's quotes.
 *
 * Only reachable while signed in; every endpoint it calls requires a session.
 *
 * The list is the CALLER's deals plus the unowned ones left over from before
 * ownership existed. The scoping happens on the server — this component never
 * sees another rep's quote and so has nothing to hide. A client appears only
 * while it still has a visible deal, which is why there is no way to remove
 * one: a company nobody is working on drops off by itself.
 */

interface Props {
  onClose: () => void;
  /** Load a stored deal into the quote form. */
  onOpen: (dealId: string) => void;
}

const FILTER_DEBOUNCE_MS = 250;

function money(n: number | null): string {
  if (n === null) return '—';
  return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-CA');
}

export function ExistingDeals({ onClose, onOpen }: Props) {
  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState<ClientWithDeals[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  // Debounced so typing in the filter does not fire a request per keystroke.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      crmApi
        .listDeals(filter.trim() || undefined)
        .then((r) => { if (!cancelled) { setRows(r.clients); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, FILTER_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [filter, reload]);

  const handleDeleteDeal = async (deal: DealSummary) => {
    if (!window.confirm(`Delete quote ${deal.id}? This cannot be undone.`)) return;
    setBusyId(deal.id);
    try {
      await crmApi.deleteDeal(deal.id);
      setReload((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Claim an unowned deal. The server decides — if another rep claimed it
   * between this list being rendered and the click, the 409 comes back as the
   * error message and the reload drops the row from the list.
   */
  const handleClaim = async (deal: DealSummary) => {
    setBusyId(deal.id);
    try {
      await crmApi.claimDeal(deal.id);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
      setReload((n) => n + 1);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.4)', display: 'flex',
        alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#F9FAFB', borderRadius: '10px', width: '100%', maxWidth: '980px',
          padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#1A1A1A', flex: 1 }}>Existing deals</h2>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{
              background: 'transparent', border: 'none', fontSize: '22px',
              color: '#6B7280', cursor: 'pointer', lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by client name, NIC, or deal ID…"
          autoFocus
          style={{
            width: '100%', padding: '10px 12px', fontSize: '14px', marginBottom: '16px',
            border: '1px solid #E5E7EB', borderRadius: '6px', outline: 'none',
            fontFamily: 'inherit', background: 'white', color: '#1A1A1A',
          }}
        />

        {error && (
          <div style={{
            padding: '10px 12px', marginBottom: '12px', borderRadius: '6px',
            background: '#FEF2F2', color: '#B00020', fontSize: '13px',
          }}>
            {error}
          </div>
        )}

        {loading && <p style={{ color: '#6B7280', fontSize: '14px' }}>Loading…</p>}

        {!loading && rows.length === 0 && (
          <p style={{ color: '#6B7280', fontSize: '14px' }}>
            {filter.trim()
              ? 'No client or quote matches that filter.'
              : 'No deals yet. Generating a PDF while signed in saves one here.'}
          </p>
        )}

        {rows.map((row) => (
          <div
            key={row.client.id}
            style={{
              background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px',
              marginBottom: '14px', overflow: 'hidden',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '12px 14px', borderBottom: '1px solid #F3F4F6',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '15px', color: '#1A1A1A' }}>
                  {row.client.company}
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  {[row.client.city, row.client.nic && `NIC ${row.client.nic}`,
                    `${row.deals.length} quote${row.deals.length === 1 ? '' : 's'}`]
                    .filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6B7280' }}>
                  <th style={{ padding: '8px 14px', fontWeight: 500 }}>Quote</th>
                  <th style={{ padding: '8px 14px', fontWeight: 500 }}>Date</th>
                  <th style={{ padding: '8px 14px', fontWeight: 500 }}>Market</th>
                  <th style={{ padding: '8px 14px', fontWeight: 500 }}>Lines</th>
                  <th style={{ padding: '8px 14px', fontWeight: 500, textAlign: 'right' }}>Monthly</th>
                  <th style={{ padding: '8px 14px' }} />
                </tr>
              </thead>
              <tbody>
                {row.deals.map((d) => (
                  <tr key={d.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '8px 14px', fontFamily: 'monospace', color: '#1A1A1A' }}>{d.id}</td>
                    <td style={{ padding: '8px 14px', color: '#6B7280' }}>{shortDate(d.createdAt)}</td>
                    <td style={{ padding: '8px 14px', color: '#6B7280' }}>{d.market ?? 'CA'}</td>
                    <td style={{ padding: '8px 14px', color: '#6B7280' }}>{d.lineCount ?? '—'}</td>
                    <td style={{ padding: '8px 14px', textAlign: 'right', color: '#1A1A1A' }}>
                      {money(d.totalMonthly)}
                    </td>
                    <td style={{ padding: '8px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {d.ownerId === null && (
                        <button
                          type="button"
                          onClick={() => handleClaim(d)}
                          disabled={busyId === d.id}
                          title="Assign this unowned quote to you. Only the first person to take it gets it."
                          style={{
                            padding: '4px 10px', fontSize: '12px', marginRight: '6px',
                            background: 'white', color: '#000E9C',
                            border: '1px solid #C7D2FE', borderRadius: '4px',
                            cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          Take ownership
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onOpen(d.id)}
                        style={{
                          padding: '4px 12px', fontSize: '12px', marginRight: '6px',
                          background: '#000E9C', color: 'white', border: 'none',
                          borderRadius: '4px', cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteDeal(d)}
                        disabled={busyId === d.id}
                        style={{
                          padding: '4px 10px', fontSize: '12px', background: 'white',
                          color: '#B00020', border: '1px solid #FECACA',
                          borderRadius: '4px', cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
